/* ------------------------------------------------------------------
   RAVA - MOTION

   Two things that snapped and now ease.

   1. The detail dropdowns. A <details> element has no open or close
      animation of its own: the content appears in one frame, which
      reads as a glitch rather than a reveal. Here the click is taken
      over, the panel's natural height measured, and the height eased
      between zero and that value. Closing keeps the element open until
      the animation finishes, otherwise the content vanishes before it
      can travel.

   2. Switching size. Each size is its own product with its own URL,
      which has to stay true for search and for the ads, so this is a
      real navigation rather than a swap in place. Left alone the
      browser tears the old page down, loads the new one and drops the
      reader at the top, far above the size buttons they just used.

      So: remember where the size row was sitting in the viewport, fade
      the page out, and on arrival put the row back at the same height
      and fade in. The reader sees the product change under a steady
      size row instead of being thrown to the top.

      Where the browser supports cross-document view transitions the
      stylesheet asks for one as well, which upgrades the fade into a
      real crossfade. Nothing here depends on it.

   Respects prefers-reduced-motion throughout: with it on, everything
   still works, it just happens at once.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var OPEN_MS = 420;
  var CLOSE_MS = 320;
  var FADE_MS = 260;

  /* --------------------------------------------------------------
     1. Accordions
     -------------------------------------------------------------- */

  function panelOf(details) {
    // Everything after the summary, wrapped once so it can be sized.
    var existing = details.querySelector(':scope > .rava-acc-panel');
    if (existing) return existing;

    var wrap = document.createElement('div');
    wrap.className = 'rava-acc-panel';

    var node = details.querySelector(':scope > summary');
    if (!node) return null;

    var moving = [];
    for (var sib = node.nextSibling; sib; sib = sib.nextSibling) moving.push(sib);
    if (!moving.length) return null;

    for (var i = 0; i < moving.length; i++) wrap.appendChild(moving[i]);
    details.appendChild(wrap);
    return wrap;
  }

  function animate(details, opening) {
    var panel = panelOf(details);
    if (!panel) return;

    if (panel.dataset.ravaAnimating === 'true') return;
    panel.dataset.ravaAnimating = 'true';

    if (opening) details.open = true;

    var target = opening ? panel.scrollHeight : 0;
    var from = opening ? 0 : panel.scrollHeight;

    panel.style.overflow = 'hidden';
    panel.style.height = from + 'px';

    // Force a reflow so the browser treats the next height as a change.
    void panel.offsetHeight;

    panel.style.transition = 'height ' + (opening ? OPEN_MS : CLOSE_MS) + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)';
    panel.style.height = target + 'px';

    var settle = function () {
      panel.removeEventListener('transitionend', settle);
      panel.style.transition = '';
      panel.style.height = '';
      panel.style.overflow = '';
      panel.dataset.ravaAnimating = 'false';
      if (!opening) details.open = false;
    };

    panel.addEventListener('transitionend', settle);
    // A missed transitionend would otherwise leave the panel stuck.
    window.setTimeout(function () {
      if (panel.dataset.ravaAnimating === 'true') settle();
    }, (opening ? OPEN_MS : CLOSE_MS) + 120);
  }

  function bindAccordions(root) {
    var list = (root || document).querySelectorAll('.rava-accordion > details');
    for (var i = 0; i < list.length; i++) {
      (function (details) {
        if (details.dataset.ravaMotion === 'true') return;
        details.dataset.ravaMotion = 'true';

        var summary = details.querySelector(':scope > summary');
        if (!summary) return;

        summary.addEventListener('click', function (e) {
          if (reduce) return;
          e.preventDefault();
          animate(details, !details.open);
        });
      })(list[i]);
    }
  }

  /* --------------------------------------------------------------
     2. Size switching

     Each size is a separate product with its own URL, and those URLs
     have to keep working: they are indexed and they are used in ads.
     But navigating to them tore the page down and built it again,
     which is why changing size felt like a reload however carefully
     the old page was faded out.

     So the URL still changes, through the History API, but the page
     does not. Only the two blocks that actually differ are fetched
     and swapped: the product information and the media gallery.
     Everything else, the header, the cart, the footer, the scripts,
     stays exactly where it is, and the scroll position never moves
     because nothing was destroyed.

     Dawn's interactive parts are custom elements, so the replacements
     upgrade themselves the moment they enter the document. Our own
     bindings are re-run explicitly below.

     Anything unexpected, a failed request, a missing block, an older
     browser, falls back to ordinary navigation, which always works.
     -------------------------------------------------------------- */

  var SWAP = ['.product__info-container', '.product__media-wrapper'];
  var switching = false;

  function canSwap() {
    return !!(window.fetch && window.DOMParser && window.history && history.pushState);
  }

  function rebind() {
    bindAccordions();
    bindSizes();
    // Let the rest of the site know the product changed, so anything else
    // watching the page can catch up.
    document.dispatchEvent(new CustomEvent('rava:productswapped'));
  }

  function swapTo(href, push) {
    if (switching) return;
    switching = true;

    if (!reduce) document.body.classList.add('rava-switching');
    var faded = new Promise(function (res) {
      window.setTimeout(res, reduce ? 0 : FADE_MS);
    });

    fetch(href, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('status ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var pairs = [];
        for (var i = 0; i < SWAP.length; i++) {
          var current = document.querySelector(SWAP[i]);
          var next = doc.querySelector(SWAP[i]);
          if (!current || !next) throw new Error('missing ' + SWAP[i]);
          pairs.push([current, next]);
        }
        return faded.then(function () { return { doc: doc, pairs: pairs }; });
      })
      .then(function (data) {
        for (var i = 0; i < data.pairs.length; i++) {
          var incoming = document.importNode(data.pairs[i][1], true);
          data.pairs[i][0].parentNode.replaceChild(incoming, data.pairs[i][0]);
        }

        if (push) history.pushState({ ravaSize: true }, '', href);

        var title = data.doc.querySelector('title');
        if (title) document.title = title.textContent;

        document.body.classList.remove('rava-switching');
        if (!reduce) {
          document.body.classList.add('rava-switch-in');
          window.setTimeout(function () {
            document.body.classList.remove('rava-switch-in');
          }, 700);
        }

        rebind();
        switching = false;
      })
      .catch(function () {
        // Never leave the reader on a half-faded page.
        document.body.classList.remove('rava-switching');
        switching = false;
        window.location.href = href;
      });
  }

  function bindSizes() {
    var links = document.querySelectorAll('.rava-sizes__btn[href]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (link.dataset.ravaSwap === 'true') continue;
      link.dataset.ravaSwap = 'true';

      link.addEventListener('click', function (e) {
        // Anything but a plain left click behaves normally, so a reader can
        // still open a size in a new tab.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        if (!canSwap()) return;

        e.preventDefault();
        swapTo(this.getAttribute('href'), true);
      });
    }
  }

  // Back and forward move between sizes the same way they arrived.
  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.ravaSize) return;
    if (!document.querySelector('.rava-sizes')) return;
    swapTo(window.location.pathname + window.location.search, false);
  });

  /* --------------------------------------------------------------
     Boot
     -------------------------------------------------------------- */

  function start() {
    bindAccordions();
    bindSizes();
    document.body.classList.add('rava-arrived');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Coming back through the history cache skips DOMContentLoaded.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) document.body.classList.remove('rava-switching');
  });

  var main = document.getElementById('MainContent');
  if (main) {
    new MutationObserver(function () { bindAccordions(main); }).observe(main, {
      childList: true, subtree: true
    });
  }
})();
